import * as React from 'react'
import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import URL from 'common/URL'
import INavigationSectionWithSubsections from '../../../Types/INavigationSectionWithSubsections'
import { Expandable } from '../../../../generic-components'
import TabLabel from './TabLabel'
import INavigationSubsection from '../../../Types/INavigationSubsection'
import SubsectionTab from './SubsectionTab'

interface ISectionWithSubsectionsTabProps {
    item: INavigationSectionWithSubsections
    currentPageSubsection: INavigationSubsection | null
    isDrawerOpen: boolean
    iconElement: React.ReactNode
    isCurrentPage: boolean
}

type TStyledExpandableProps = React.ComponentProps<typeof Expandable> & {
    isCurrentPage: boolean
}

const StyledExpandable = styled<React.FC<TStyledExpandableProps>>(Expandable, {
    shouldForwardProp: (prop: keyof TStyledExpandableProps) => prop !== 'isCurrentPage',
})`
    && .Mui-expanded {
        min-height: 40px;
        max-height: 40px;
        margin-top: 0;
        margin-bottom: 0;
        align-items: center;
    }

    && .MuiAccordionSummary-content {
        margin: 0;
    }

    && .MuiAccordionSummary-root {
        padding: 0 10px 0 0;
        min-height: 36px;
        max-height: 36px;
        margin-bottom: 4px;
        border-radius: ${({ theme }) => theme.tokens.radiusDefault};
        transition: background-color ${({ theme }) => theme.tokens.transitionQuick};

        ${({ isCurrentPage, theme }) =>
            isCurrentPage &&
            `
            background-color: ${theme.colors.primaryTeal30};
        `}

        :hover {
            background-color: ${({ isCurrentPage, theme }) => !isCurrentPage && theme.colors.primaryCyan60};
        }
    }

    && .MuiAccordionDetails-root {
        flex-direction: column;
        padding: 0;
    }
`

const ExpandableItemContainer = styled.div`
    width: 312px;
`

const useExpansion = (isDrawerOpen: boolean) => {
    const [isExpanded, setIsExpanded] = useState(false)

    const handleExpansionToggle = () => {
        setIsExpanded((currentIsExpanded) => !currentIsExpanded)
    }

    useEffect(() => {
        isDrawerOpen ? setIsExpanded(true) : setIsExpanded(false)
    }, [isDrawerOpen])

    return {
        isExpanded,
        handleExpansionToggle,
    }
}

const SectionWithSubsectionsTab: React.FC<ISectionWithSubsectionsTabProps> = ({
    isDrawerOpen,
    item,
    currentPageSubsection,
    iconElement,
    isCurrentPage,
}) => {
    const { isExpanded, handleExpansionToggle } = useExpansion(isDrawerOpen)

    if (!isDrawerOpen) {
        return (
            <TabLabel
                isCurrentPage={isCurrentPage}
                to={URL.oldClient(item.subsectionObjects[0].Url)}
                variant="h4"
                sectionName={item.Name}
            >
                {iconElement}
            </TabLabel>
        )
    }

    return (
        <ExpandableItemContainer>
            <StyledExpandable
                isCurrentPage={isCurrentPage}
                label={
                    <TabLabel
                        isCurrentPage={isCurrentPage}
                        to={URL.oldClient(item.subsectionObjects[0].Url)}
                        variant="h4"
                        sectionName={item.Name}
                        isDrawerOpen={isDrawerOpen}
                    >
                        {iconElement}
                        {item.Translation}
                    </TabLabel>
                }
                muiExpansionPanelProps={{ expanded: isExpanded, onChange: handleExpansionToggle }}
            >
                {item.subsectionObjects.map((subsection) => (
                    <SubsectionTab
                        currentPageSubsection={currentPageSubsection}
                        key={subsection.Url}
                        subsection={subsection}
                        to={URL.oldClient(subsection.Url)}
                    >
                        {subsection.Translation}
                    </SubsectionTab>
                ))}
            </StyledExpandable>
        </ExpandableItemContainer>
    )
}

export default SectionWithSubsectionsTab
